import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { get, set, del } from "idb-keyval";
import "@/index.css";
import "@/lib/pwa";
import App from "@/App";

const MONTH = 30 * 24 * 60 * 60 * 1000;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: Infinity, // MONTH overflows setTimeout (max ~24.8 days) and GCs queries instantly
      networkMode: "offlineFirst",
      refetchOnWindowFocus: false,
      retry: (count, err) => !!err?.response && err.response.status >= 500 && count < 1,
    },
    mutations: { networkMode: "always" },
  },
});

export const persister = createAsyncStoragePersister({
  storage: { getItem: (k) => get(k), setItem: (k, v) => set(k, v), removeItem: (k) => del(k) },
  key: "rollsmartr-cache",
  throttleTime: 1500,
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: MONTH,
        buster: "v1",
        dehydrateOptions: { shouldDehydrateQuery: (q) => q.state.status === "success" && q.queryKey[0] !== "quota" },
      }}
    >
      <App />
    </PersistQueryClientProvider>
  </React.StrictMode>,
);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).then((r) => r.update()).catch(() => {}));
  navigator.serviceWorker.addEventListener("message", (e) => {
    if (e.data?.type !== "rs:navigate") return;
    const path = new URL(e.data.url).pathname;
    if (window.location.pathname !== path) window.location.assign(e.data.url);
  });
  const hadController = !!navigator.serviceWorker.controller;
  let reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (hadController && !reloaded) {
      reloaded = true;
      window.location.reload();
    }
  });
}
